import { Box, Button, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface AmountInputProps {
  amount: string;
  balance: number;
  isAuthenticated: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickAdd: (addAmount: number | 'max') => void;
}

export function AmountInput({
  amount,
  balance,
  isAuthenticated,
  onChange,
  onQuickAdd,
}: AmountInputProps) {
  const { t } = useT();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {t('withdrawModal.amountLabel', { defaultValue: 'Amount' })}
        </Typography>
        {isAuthenticated && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              {t('trade.balanceLabel', { defaultValue: 'Balance' })}: ${balance.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        value={amount}
        onChange={onChange}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        sx={{
          mb: 1.5,
          '& input': {
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '1.5rem',
            py: 1,
            color: 'text.secondary',
          },
        }}
      />
      <Box sx={{ display: 'flex', gap: 0.75 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onQuickAdd(1)}
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.75,
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          +$1
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onQuickAdd(20)}
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.75,
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          +$20
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onQuickAdd(100)}
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.75,
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          +$100
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onQuickAdd('max')}
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
            py: 0.75,
            borderColor: 'divider',
            color: 'text.secondary',
            borderRadius: '6px',
            textTransform: 'none',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          {t('trade.max', { defaultValue: 'Max' })}
        </Button>
      </Box>
    </Box>
  );
}
